import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import Linked from 'shared-components/service/Linked'
import { publicConfig } from 'config'
import type { CommunityStanding } from 'lib/community/types'
import {
  EMPTY_DRAFT,
  phonePublishLine,
  type CommunityPointsTable,
  type OwnerNoteDraft,
  type OwnerNoteSeed,
} from 'lib/community/review'
import {
  parseStoredDraft,
  reviewDraftKey,
  reviewPhotosKey,
  reviewPublishedKey,
  writerBack,
  writerHref,
  type WriterHandoff,
} from 'lib/community/writer-handoff'
import type {
  OwnerClaimResponse,
  OwnerNoteOwn,
  OwnerPublishResponse,
  OwnerQuestionSetDef,
} from 'lib/dealhunt/types'
import { useOwnerTrack, type OwnerTrack } from 'lib/owner-notes/analytics'
import { askOwnersSeed, GATE_LOGIN_DIALOG } from 'lib/owner-notes/band-copy'
import { signInGateFor, SignInGateContext } from 'lib/owner-notes/signin-gate'
import { useOwnerSessionOptional } from 'lib/owner-notes/auth-bridge'
import { hasDeviceMarker } from 'lib/owner-notes/declared'
import { sharedMe } from 'lib/owner-notes/api'
import { ownNoteFromMe, useOwnNote } from 'lib/owner-notes/own-note'
import { loadQuestionSet, peekQuestionSet } from 'lib/owner-notes/questions'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import { useNoteBand } from 'components/owner-notes/useNoteBand'
import type { PublishPhotos } from 'components/owner-notes/OwnerNoteBand'
import OwnPicker from './OwnPicker'
import ReviewPreview from './ReviewPreview'
import ReviewPublishPanel from './ReviewPublishPanel'
import RewardScreen from './RewardScreen'
import base from './community.module.css'
import styles from './Review.module.css'

export interface ReviewProduct {
  nodeId: string
  name: string
  /** The SoundGuys product slug (what follows `/product/`), for the thread link. */
  slug: string
  /** The product page. */
  href: string
  imageUrl: string | null
  brand: string | null
  /** Published owner reviews on this product when the page was rendered. */
  publishedCount: number
}

interface ReviewComposerProps {
  product: ReviewProduct
  points: CommunityPointsTable | null
  founding: { count: number; cap: number } | null
  /** The validated handoff params (`writerHandoffParams`): where the reader came from. */
  handoff: WriterHandoff
  /** Edit mode is known only after `/me`: the page's breadcrumb follows it. */
  onEditingKnown?: (editing: boolean) => void
}

/** `sessionStorage`, never throwing: private windows and blocked storage just lose the draft. */
function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}
function writeSession(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key)
    } else {
      window.sessionStorage.setItem(key, value)
    }
  } catch {
    // Storage full or blocked: the draft is a convenience.
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Blocked storage: the origin tab re-reads on its next load instead.
  }
}

/** What the reward screen needs from the accepted publish, frozen when it arrives. */
interface Accepted {
  result: OwnerPublishResponse
  /** `/me`'s standing before this publish — for "Was <tier>". */
  before: CommunityStanding | null
  /** The review carries public photos after this publish (the reward screen's "Photo" line). */
  photo: boolean
  /** The reader's photos did not attach (no signed profile at send time). */
  photosDropped: boolean
  /** "Ask the other owners something": seeded from the answers sent (never the sentence). */
  seed: string
}

/**
 * The form page's body (design 5b) and, after an accepted publish, the reward screen (5c).
 *
 * The band is the product page's (`OwnerNoteBand`, lazy through `useNoteBand`), mounted only
 * once `/me` has settled: it reads `existing` in its first render, so a reader who already has
 * a review on this product must arrive in edit mode, never as a second POST. The edit/new
 * choice is frozen at that moment — `applyResult` later replaces the own note, and the band
 * must not remount mid-result.
 *
 * The handoff (contract §7): `?buy_again=` seeds the band's buy-again answer, ×/Close/Done and
 * the reward's "Back …" go to `?from=` (`writerBack`), `?src=` is the open's `location`. A new
 * review's draft is kept in `sessionStorage` (`reviewDraftKey`) and restored through the band's
 * `seed` — never in edit mode. Any publish answer clears it and sets `reviewPublishedKey`, which
 * tells the page the reader returns to that its own-note state is stale (`own-note.ts`).
 *
 * Analytics: `community_page_owner_*` (`useOwnerTrack(product, 'community_page')`). The band's
 * `location` prop stays `section` (it drives the band's layout: no strip intro here); its
 * `band_opened` is re-labelled with the handoff's `src` (`community` without one), so an ask's
 * opens sit under that ask's impressions on its own page.
 */
const ReviewComposer: React.VFC<ReviewComposerProps> = ({
  product,
  points,
  founding,
  handoff,
  onEditingKnown,
}) => {
  const router = useRouter()
  const trailingSlash = publicConfig('useTrailingSlash')
  const trackProduct = useMemo(
    () => ({ nodeId: product.nodeId, name: product.name }),
    [product.nodeId, product.name]
  )
  const trackPage = useOwnerTrack(trackProduct, 'community_page')
  const openLocation = handoff.src ?? 'community'
  const trackOwner = useCallback<OwnerTrack>(
    (action, params) =>
      trackPage(action, action === 'band_opened' ? { ...params, location: openLocation } : params),
    [trackPage, openLocation]
  )
  const back = writerBack(handoff, trailingSlash)
  const draftKey = reviewDraftKey(product.slug)
  const session = useOwnerSessionOptional()
  const { me, applyResult, applyClaim, clearDevice } = useOwnNote(product.nodeId)
  const band = useNoteBand(product.nodeId)

  // Frozen once `/me` has answered (or there is no device): `undefined` = not decided yet. A
  // direct read rather than `useOwnNote`'s `pending`: when `sharedMe` answers from its cache
  // (the reader came from /community within 30 s), `pending` rises and falls in one batch and
  // is never seen raised. The read shares the cache, so it costs no second request.
  const [existing, setExisting] = useState<OwnerNoteOwn | null | undefined>(undefined)
  // A new review's restored draft, read in the same update that settles `existing` — the band
  // reads its seed on its first render only.
  const [seed, setSeed] = useState<OwnerNoteSeed | null>(null)
  useEffect(() => {
    const settle = (own: OwnerNoteOwn | null): void => {
      if (!own) {
        setSeed(parseStoredDraft(readSession(draftKey)))
      }
      setExisting(own)
    }
    if (!hasDeviceMarker()) {
      settle(null)
      return undefined
    }
    let live = true
    sharedMe().then(
      res => live && settle(ownNoteFromMe(res, product.nodeId)),
      () => live && settle(null)
    )
    return () => {
      live = false
    }
  }, [product.nodeId, draftKey])
  useEffect(() => {
    if (existing !== undefined) {
      onEditingKnown?.(existing !== null)
    }
  }, [existing, onEditingKnown])

  const { open: openBand } = band
  useEffect(() => {
    if (existing !== undefined) {
      openBand(existing !== null)
    }
  }, [existing, openBand])

  // The preview's labels: the set the band loads, shared in flight (no second request).
  const [questions, setQuestions] = useState<OwnerQuestionSetDef | null>(() =>
    peekQuestionSet(product.nodeId)
  )
  useEffect(() => {
    let live = true
    loadQuestionSet(product.nodeId).then(
      qs => {
        if (live) {
          setQuestions(qs)
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [product.nodeId])

  const [draft, setDraft] = useState<OwnerNoteDraft>(EMPTY_DRAFT)
  const draftRef = useRef(draft)
  draftRef.current = draft
  // Kept for a new review only, and only until DealHunt has the note (any publish answer).
  const keepDraft = useRef(true)
  if (existing) {
    keepDraft.current = false
  }
  const onDraftChange = useCallback(
    (next: OwnerNoteDraft): void => {
      setDraft(next)
      if (keepDraft.current) {
        writeSession(draftKey, JSON.stringify({ answers: next.answers, note: next.note }))
      }
    },
    [draftKey]
  )
  const [picking, setPicking] = useState(false)
  const [claim, setClaim] = useState<OwnerClaimResponse | null>(null)
  const [accepted, setAccepted] = useState<Accepted | null>(null)

  const onResult = useCallback(
    (result: OwnerPublishResponse, photos?: PublishPhotos): void => {
      applyResult(result)
      // Live or waiting for a moderator: the draft is done. A rejection or a hold keeps it — the
      // reader may still "Edit and resend", and a reload should not lose the text. The page the
      // reader goes back to re-reads its own-note state either way (`own-note.ts`).
      const { outcome } = result.verdict
      if (outcome === 'accept' || outcome === 'review') {
        keepDraft.current = false
        writeSession(draftKey, null)
      }
      // The band forgets its stored photo set on any answer too; cleared here as well in case
      // the band has already unmounted.
      writeSession(reviewPhotosKey(product.slug), null)
      writeSession(reviewPublishedKey(product.slug), '1')
      // A new-tab handoff (`?tab=new`): the origin is another tab, and sessionStorage is per tab
      // — the same mark in localStorage reaches it (its `storage` event, `own-note.ts`).
      if (handoff.newTab) {
        writeLocal(reviewPublishedKey(product.slug), '1')
      }
      // The reward screen needs the community block; without it (an older DealHunt) the band's
      // own result stays, as it does for review / reject / hold — and for a plain edit of a
      // review that was already live, which earned nothing to celebrate.
      const earned = result.community
      const wasLive = existing?.status === 'published'
      if (
        result.verdict.outcome === 'accept' &&
        earned &&
        (!wasLive || earned.points_awarded > 0)
      ) {
        // `me` is this render's: the one before the publish (a claim on Publish patches later).
        setAccepted({
          result,
          before: me?.community ?? null,
          // The band says what went; an older band (no second argument) — the draft's prediction.
          photo: photos ? photos.attached : draftRef.current.hasPhoto,
          photosDropped: photos?.dropped ?? false,
          seed: askOwnersSeed(
            product.name,
            draftRef.current.answers.reads,
            questions?.reads ?? null
          ),
        })
        window.scrollTo({ top: 0 })
      }
    },
    [applyResult, me, existing, draftKey, product.slug, product.name, questions, handoff.newTab]
  )
  const onClaimed = useCallback(
    (result: OwnerClaimResponse): void => {
      applyClaim(result)
      setClaim(result)
    },
    [applyClaim]
  )
  const onClose = useCallback((): void => {
    void router.push(back.href)
  }, [router, back.href])

  if (accepted) {
    return (
      <RewardScreen
        product={product}
        result={accepted.result}
        before={accepted.before}
        claim={claim}
        linked={Boolean(claim) || Boolean(me?.profile.claimed)}
        seed={accepted.seed}
        trackOwner={trackOwner}
        onClaimed={onClaimed}
        photo={accepted.photo}
        photosDropped={accepted.photosDropped}
        points={points}
        back={back}
        newTab={handoff.newTab}
      />
    )
  }

  const editing = Boolean(existing)
  const signedIn = session.signedIn || Boolean(me?.profile.claimed)
  // The panel's gate: until the session check has answered, a signed-in reader would flash the
  // signed-out panel.
  const panelReady = existing !== undefined && (session.resolved || Boolean(me?.profile.claimed))
  const panelInput = {
    productName: product.name,
    points,
    founding,
    publishedCount: product.publishedCount,
    standing: me?.community ?? null,
    signedIn,
    counts: existing?.status !== 'published',
    hasPhoto: draft.hasPhoto,
  }
  // The sign-in gate (always on, build-time, so the server pass and hydration agree; every
  // handed-off ask carries the notice too): the band reads it from the context; the phone line and the panel —
  // client-only, after `panelReady` — take the gated copy for a new review (an edit is never
  // gated). `src=reminder` has no ask, so these two are that reader's only notice before step 1.
  const gate = signInGateFor('community')
  const gatedCopy = gate && !editing
  const phoneLine = panelReady ? phonePublishLine(panelInput, { gated: gatedCopy }) : null
  // Gated, the dialog says what this sign-in is for (a review, not comments).
  const signIn = (): Promise<void> =>
    session.showLogIn(gatedCopy ? { copy: GATE_LOGIN_DIALOG } : undefined)
  const { OwnerNoteBand } = band
  let form: React.ReactNode = <p className={styles.loading}>{'Loading the form…'}</p>
  if (!turnstileConfigured()) {
    // Production DealHunt refuses a publish without a bot-check token (Constraint 73c27437).
    form = <p className={styles.loading}>{'Owner reviews can’t be sent from this site yet.'}</p>
  } else if (band.bandOpen && OwnerNoteBand && existing !== undefined) {
    form = (
      <SignInGateContext.Provider value={gate}>
        <OwnerNoteBand
          product={{ ...trackProduct, href: product.href }}
          existing={existing}
          location="section"
          trackOwner={trackOwner}
          onClose={onClose}
          onResult={onResult}
          onClaimed={onClaimed}
          me={me}
          onDeviceForgotten={clearDevice}
          onDraftChange={onDraftChange}
          seed={existing ? null : seed}
          photoDraftKey={existing ? null : reviewPhotosKey(product.slug)}
          seedBuyAgain={handoff.buyAgain ?? undefined}
          hideTitle
          // The page prints its own preview and publish panel (the aside below).
          hostPanels
        />
      </SignInGateContext.Provider>
    )
  }

  return (
    <>
      <header className={base.head}>
        <h1 className={styles.h1}>
          {editing
            ? `Edit your owner review of the ${product.name}`
            : `Your owner review of the ${product.name}`}
        </h1>
        <div className={styles.product}>
          {product.imageUrl && (
            // eslint-disable-next-line @next/next/no-img-element
            <img className={styles.thumb} src={product.imageUrl} alt="" loading="lazy" />
          )}
          <Linked href={product.href} className={styles.productName}>
            {product.name}
          </Linked>
          {/* The picker opens in place; a pick keeps where the reader came from. */}
          <button
            type="button"
            className={styles.change}
            aria-expanded={picking}
            aria-controls="review-change"
            onClick={() => setPicking(p => !p)}
          >
            {picking ? 'Cancel' : 'Change'}
          </button>
        </div>
        {picking && (
          <div id="review-change" className={styles.changeRow}>
            <OwnPicker
              id="review-change-results"
              autoFocus
              hrefFor={slug =>
                writerHref({ product: slug, src: handoff.src, from: handoff.from }, trailingSlash)
              }
            />
          </div>
        )}
      </header>
      <div className={base.columns}>
        <div className={base.main}>
          {/* A phone stacks the rail under the form: what publishing earns, and the sign-in
              that earns it, are said here first (the full panel stays below). */}
          {phoneLine && (
            <div className={styles.phonePublish}>
              <p className={styles.phonePublishText}>{phoneLine.text}</p>
              {phoneLine.signIn && (
                <button
                  type="button"
                  className={base.btn}
                  // The panel below has its own "Sign in": this one says where it is (gated, the
                  // line beside it says the sign-in can come now or at Publish).
                  aria-label={gatedCopy ? 'Sign in now' : 'Sign in before you publish'}
                  onClick={() => void signIn()}
                >
                  {'Sign in'}
                </button>
              )}
            </div>
          )}
          {form}
        </div>
        <aside className={base.side}>
          <ReviewPreview
            draft={draft}
            questions={questions}
            productName={product.name}
            handle={me?.profile.handle ?? null}
            tier={me?.community?.member ? me.community.tier : null}
          />
          {panelReady && <ReviewPublishPanel {...panelInput} gated={gatedCopy} onSignIn={signIn} />}
        </aside>
      </div>
    </>
  )
}

export default ReviewComposer
